import { Fragment, useMemo } from 'react';

import { MatrixCell } from '../components/MatrixCell.tsx';
import { MatrixScrollBox } from '../components/MatrixScrollBox.tsx';
import type { TooltipContent } from '../components/Tooltip.tsx';
import {
  ColumnHeader,
  GroupedCompLabel,
  NestConnector,
  PropIndicator,
  TruncatedLabel,
  nestVariant,
} from '../components/TruncatedLabel.tsx';
import {
  NO_EXPANDED,
  RATING_LABELS,
  buildDerivedContextFunctionGate,
  buildRatingIndex,
  buildSkillFunctions,
  compRowHeight,
  indexSkills,
  isCompactCatalog,
  isInstitutional,
  introductionLabel,
  levelClass,
  ownershipLabel,
  remapSkillFunctionsToParents,
  rollupColumns,
  type BusinessFunction,
  type DerivedContextFunctionGate,
  type DerivedContext,
  type Engineer,
  type IntroductionIndex,
  type OwnershipIndex,
  type Rating,
  type RatingIndex,
  type RatingValue,
  type RollupColumn,
  type Skill,
} from '../data-views.ts';
import { sortByClientDistance } from '../derived-context-order.ts';
import { profileCellRating } from '../talent-profile.ts';

/**
 * Single-engineer "Talent Profile" platform map. Mirrors the Resilience map's
 * structure — derivedContexts × business functions, with the same "Group Skills"
 * toggle: grouped (one row per derivedContext) vs ungrouped (per-skill rows with
 * tree connectors + proprietary indicators). The column axis is the promoted,
 * top-level function set on every map, no toggle (see `data-views.ts`).
 * The only differences from Resilience: cells are shaded in GREEN by THIS
 * engineer's proficiency (not team coverage), and the numeric rating is hidden
 * — dark green = expert, light green = apprentice; the level is on hover.
 */

// The four frozen label columns (comp name / connector / prop dot / skill),
// matching the Resilience grid; cumulative left offsets (+ the 6px gap).
const FREEZE_LEFTS = [0, 136, 184, 196];

/**
 * Cell props for a proficiency rating: GREEN shade, no number (value stays
 * null so the cell paints its level class but renders no text). A missing
 * rating is an empty `na` cell.
 */
function greenCell(
  rating: RatingValue | null,
  tooltip: TooltipContent | null,
): { value: number | null; levelClass: string; tooltip: TooltipContent | null } {
  if (rating == null) return { value: null, levelClass: 'na', tooltip: null };
  return { value: null, levelClass: `green ${levelClass(rating)}`, tooltip };
}

/** Pick the adjacency map a column's cells should use — the parent-remapped
 *  map for `group` columns (aggregate across the group), else the flat map. */
function mapForColumn(
  col: RollupColumn,
  flat: Map<string, Set<string>>,
  grouped: Map<string, Set<string>>,
): Map<string, Set<string>> {
  return col.kind === 'group' ? grouped : flat;
}

interface RowProps {
  comp: DerivedContext;
  columns: RollupColumn[];
  engineerId: string;
  ratingIndex: RatingIndex;
  /** Companion signal A — per-(engineer, institutional skill) live-file ownership.
   *  Absent for orgs with no proprietary skills (no badge shown). */
  ownershipIndex?: OwnershipIndex;
  /** Companion signal B — per-(engineer, skill) new-module / skill introduction. */
  introductionIndex?: IntroductionIndex;
  skillFunctions: Map<string, Set<string>>;
  skillFunctionsGrouped: Map<string, Set<string>>;
  /** Shared platform-map cell gate — a cell exists only where the derivedContext
   *  itself serves the function (blocks shared-skill phantoms). */
  cfGate: DerivedContextFunctionGate;
}

/** Ungrouped: one row per skill, with the tree connector + proprietary dot. */
function ProfileFlatGroup({
  comp,
  columns,
  engineerId,
  ratingIndex,
  ownershipIndex,
  introductionIndex,
  cfGate,
}: RowProps) {
  const multiSkill = comp.skills.length > 1;
  return (
    <>
      {comp.skills.map((skill, i) => (
        <Fragment key={skill.id}>
          {i === 1 && multiSkill ? null : (
            <div
              className={`snap-grid__row-label snap-grid__row-label--muted is-frozen${
                i === 0 && multiSkill ? ' snap-grid__row-label--wrap-2' : ''
              }`}
              style={
                i === 0 && multiSkill
                  ? { gridRow: 'span 2', left: FREEZE_LEFTS[0] }
                  : { left: FREEZE_LEFTS[0] }
              }
            >
              {i === 0 ? <TruncatedLabel text={comp.display} max={multiSkill ? 60 : 15} /> : null}
            </div>
          )}
          <div
            className='snap-grid__row-label snap-grid__row-label--conn is-frozen'
            style={{ left: FREEZE_LEFTS[1] }}
          >
            <NestConnector variant={nestVariant(i, comp.skills.length)} />
          </div>
          <div
            className='snap-grid__row-label snap-grid__row-label--prop is-frozen'
            style={{ left: FREEZE_LEFTS[2] }}
          >
            <PropIndicator institutional={isInstitutional(skill)} />
          </div>
          <div
            className='snap-grid__row-label is-frozen is-frozen-edge'
            style={{ left: FREEZE_LEFTS[3] }}
          >
            <TruncatedLabel text={skill.display} />
            {(() => {
              // Ownership badge (companion signal A): shown on PROPRIETARY skills
              // only, where "who built our unique thing" is legible. Surfaces
              // builders the volume-based rung under-credits on small footprints.
              if (!isInstitutional(skill)) return null;
              const label = ownershipLabel(ownershipIndex?.get(engineerId)?.get(skill.id));
              return label ? <span className='snap-grid__owner-badge'>{label}</span> : null;
            })()}
            {(() => {
              // Introduction badge (companion signal B): "Introduced" / "Built N
              // modules" — who brought the capability in. All skill kinds.
              const label = introductionLabel(introductionIndex?.get(engineerId)?.get(skill.id));
              return label ? <span className='snap-grid__intro-badge'>{label}</span> : null;
            })()}
          </div>
          {columns.map((col) => {
            // Shared gate: this skill's rating shows under a function only when
            // the derivedContext itself serves it (not via the per-skill union).
            const reaches = cfGate.serves(comp.id, col);
            const rating = reaches ? (ratingIndex.get(engineerId)?.get(skill.id) ?? null) : null;
            const tooltip: TooltipContent | null =
              rating == null ? null : { title: skill.display, sub: RATING_LABELS[rating] };
            return <MatrixCell key={col.fn.id} {...greenCell(rating, tooltip)} />;
          })}
        </Fragment>
      ))}
    </>
  );
}

/** Grouped: one row per derivedContext, cell = the engineer's best rating in it. */
function ProfileGroupedRow({
  comp,
  columns,
  engineerId,
  ratingIndex,
  skillFunctions,
  skillFunctionsGrouped,
  cfGate,
}: RowProps) {
  return (
    <>
      <GroupedCompLabel
        frozen
        freezeLefts={FREEZE_LEFTS}
        propCount={comp.skills.filter(isInstitutional).length}
        name={<TruncatedLabel text={comp.display} max={22} />}
        count={
          <>
            {comp.skills.length} skill{comp.skills.length === 1 ? '' : 's'}
          </>
        }
      />
      {columns.map((col) => {
        // Shared gate: only a genuinely-served (comp × fn) cell shows a rating;
        // within it, profileCellRating counts the derivedContext's skills as before.
        const rating = cfGate.serves(comp.id, col)
          ? profileCellRating(
              comp,
              col.fn.id,
              engineerId,
              ratingIndex,
              mapForColumn(col, skillFunctions, skillFunctionsGrouped),
            )
          : null;
        const tooltip: TooltipContent | null =
          rating == null ? null : { title: comp.display, sub: RATING_LABELS[rating] };
        return (
          <MatrixCell key={col.fn.id} {...greenCell(rating, tooltip)} extraClass='is-merged' />
        );
      })}
    </>
  );
}

export interface ProfileMapProps {
  engineer: Engineer;
  derivedContexts: DerivedContext[];
  businessFunctions: BusinessFunction[];
  ratings: Rating[];
  /** Companion signal A — per-(engineer, institutional skill) live-file ownership.
   *  Optional so other callers (e.g. Timeline) render the map without it. */
  ownershipIndex?: OwnershipIndex;
  /** Companion signal B — per-(engineer, skill) new-module / skill introduction. */
  introductionIndex?: IntroductionIndex;
  grouped: boolean;
  techVisible: (skill: Skill) => boolean;
}

/**
 * The bare profile matrix grid (no scroll wrapper): derivedContexts × business
 * functions for ONE engineer, green-shaded, numeric ratings hidden. Reused by
 * the Talent Profile (wrapped in `MatrixScrollBox`) and the Timeline
 * single-person mode (wrapped in the Timeline's scrubber-clearing scroll box).
 */
export function ProfileMatrixGrid({
  engineer,
  derivedContexts,
  businessFunctions,
  ratings,
  ownershipIndex,
  introductionIndex,
  grouped,
  techVisible,
}: ProfileMapProps) {
  // Guard: a stale cached talent view (pre-`business_functions`) would arrive
  // without the field — default to [] so we degrade to an empty map rather
  // than crashing the page.
  const functions = useMemo(() => businessFunctions ?? [], [businessFunctions]);
  // Client-distance row order, with each derivedContext's skills narrowed by the
  // tech filter — identical to ResilienceView's `visibleComps`, so row counts
  // (and thus `compRowHeight`) match the Resilience map exactly.
  const visibleComps = useMemo(
    () =>
      sortByClientDistance(derivedContexts)
        .map((c) => ({ ...c, skills: c.skills.filter(techVisible) }))
        .filter((c) => c.skills.length > 0),
    [derivedContexts, techVisible],
  );
  const skillFunctions = useMemo(() => buildSkillFunctions(derivedContexts), [derivedContexts]);
  const ratingIndex = useMemo(
    () => buildRatingIndex(indexSkills(derivedContexts), [engineer], ratings),
    [derivedContexts, engineer, ratings],
  );

  // The column axis: the promoted, top-level functions, each parent collapsed
  // over its children. `skillFunctionsGrouped` remaps children onto their
  // parent so a group column aggregates the whole group. Matches
  // ResilienceView exactly.
  const columns = useMemo<RollupColumn[]>(
    () => rollupColumns(functions, NO_EXPANDED),
    [functions],
  );
  const skillFunctionsGrouped = useMemo(
    () => remapSkillFunctionsToParents(skillFunctions, functions),
    [skillFunctions, functions],
  );
  // Shared platform-map cell gate — a cell exists only where the derivedContext
  // itself serves the function (its own edges), never via the per-skill union.
  const cfGate = useMemo(
    () => buildDerivedContextFunctionGate(derivedContexts, functions),
    [derivedContexts, functions],
  );

  const gridStyle = useMemo(() => {
    const base: Record<string, string> = {
      gridTemplateColumns: `130px 42px 6px 140px repeat(${columns.length}, minmax(72px, 1fr))`,
    };
    if (grouped) {
      const compact = isCompactCatalog(derivedContexts);
      base.gridTemplateRows = `auto ${visibleComps.map((c) => compRowHeight(c.skills.length, compact) + 'px').join(' ')}`;
    }
    return base;
  }, [columns.length, grouped, visibleComps, derivedContexts]);

  return (
    <div className='snap-grid' style={gridStyle}>
      <div className='snap-grid__freeze-corner is-frozen' style={{ left: FREEZE_LEFTS[0] }} />
      <div className='snap-grid__freeze-corner is-frozen' style={{ left: FREEZE_LEFTS[1] }} />
      <div className='snap-grid__freeze-corner is-frozen' style={{ left: FREEZE_LEFTS[2] }} />
      <div
        className='snap-grid__freeze-corner is-frozen is-frozen-edge'
        style={{ left: FREEZE_LEFTS[3] }}
      />
      {columns.map((col) => (
        <ColumnHeader key={col.fn.id} display={col.fn.display} subtitle={col.fn.subtitle} />
      ))}

      {grouped
        ? visibleComps.map((comp) => (
            <ProfileGroupedRow
              key={comp.id}
              comp={comp}
              columns={columns}
              engineerId={engineer.id}
              ratingIndex={ratingIndex}
              skillFunctions={skillFunctions}
              skillFunctionsGrouped={skillFunctionsGrouped}
              cfGate={cfGate}
            />
          ))
        : visibleComps.map((comp) => (
            <ProfileFlatGroup
              key={comp.id}
              comp={comp}
              columns={columns}
              engineerId={engineer.id}
              ratingIndex={ratingIndex}
              ownershipIndex={ownershipIndex}
              introductionIndex={introductionIndex}
              skillFunctions={skillFunctions}
              skillFunctionsGrouped={skillFunctionsGrouped}
              cfGate={cfGate}
            />
          ))}
    </div>
  );
}

/** Talent Profile: the profile grid in the standard matrix scroll box. */
export function TalentProfileMap(props: ProfileMapProps) {
  return (
    <MatrixScrollBox>
      <ProfileMatrixGrid {...props} />
    </MatrixScrollBox>
  );
}
